import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useAuth } from './AuthProvider';
import GoogleIcon from '@mui/icons-material/Google';
import { useLocation, useNavigate } from 'react-router-dom';

export const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp] = useState(false);
  const [useMagic] = useState(false); // Changed to false - default to password login
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaFailedAttempts, setMfaFailedAttempts] = useState(0);
  const [mfaCooldownUntilMs, setMfaCooldownUntilMs] = useState(0);
  // Suppresses auto-redirect after a session/JWT error so the user sees the
  // "session expired" message and can re-enter credentials.
  const [sessionExpired, setSessionExpired] = useState(false);

  const {
    user,
    roleLoading,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithMagicLink,
    sendPasswordReset,
    mfaChallengeRequired,
    clearMfaChallengeState,
    verifyMfaChallenge,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If the user is authenticated and not in an MFA or password recovery flow,
    // automatically redirect away from the standalone /login page.
    // Also wait for roleLoading to complete to prevent navigating before
    // RoleBasedRedirect has the data it needs.
    if (
      user &&
      !loading &&
      !roleLoading &&
      !mfaChallengeRequired &&
      !sessionExpired &&
      location.pathname === '/login'
    ) {
      navigate('/', { replace: true });
    }
  }, [
    user,
    loading,
    roleLoading,
    mfaChallengeRequired,
    sessionExpired,
    location.pathname,
    navigate,
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSessionExpired(false);
    setLoading(true);
    setError(null);

    try {
      if (useMagic) {
        await signInWithMagicLink(email);
      } else if (isSignUp) {
        await signUp(email, password);
      } else {
        await signIn(email, password);
        // Redirect immediately after successful sign-in (don't wait for auth state change),
        // but do NOT redirect if MFA step-up is required.
        // Don't navigate here — the useEffect handles redirect
        // after role is loaded, preventing the race condition.
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReset = async () => {
    setLoading(true);
    setError(null);
    try {
      await sendPasswordReset(resetEmail);
      setResetOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    const now = Date.now();
    if (mfaCooldownUntilMs > now) {
      const remainingSeconds = Math.max(1, Math.ceil((mfaCooldownUntilMs - now) / 1000));
      setError(`Please wait ${remainingSeconds}s before trying again.`);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await verifyMfaChallenge(mfaCode);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid MFA code';
      const normalized = String(message).toLowerCase();

      // Distinguish JWT/session errors from actual wrong TOTP codes.
      // "invalid claim: missing sub claim" means SDK lost session, not a wrong code.
      if (
        normalized.includes('claim') ||
        normalized.includes('bad_jwt') ||
        normalized.includes('missing sub')
      ) {
        // Set sessionExpired BEFORE clearing MFA state so the auto-redirect
        // useEffect doesn't navigate to / when mfaChallengeRequired becomes null.
        setSessionExpired(true);
        setError('Your session expired. Please sign in again.');
        clearMfaChallengeState();
        setMfaCode('');
        setMfaFailedAttempts(0);
        setMfaCooldownUntilMs(0);
      } else if (normalized.includes('invalid')) {
        const nextAttempt = mfaFailedAttempts + 1;
        // Short, user-friendly cooldown to prevent accidental hammering (not a hard lockout).
        const schedule = [0, 1000, 2000, 5000, 10000, 15000, 30000];
        const idx = Math.min(nextAttempt, schedule.length - 1);
        const delayMs = schedule[idx] ?? 0;
        setMfaFailedAttempts(nextAttempt);
        setMfaCooldownUntilMs(Date.now() + delayMs);
        setError('Invalid code. Please check and try again.');
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="background.default"
    >
      <Card sx={{ maxWidth: 420, width: '100%', mx: 2 }}>
        <CardHeader
          title={
            <Typography variant="h4" component="h1" textAlign="center">
              Chargaux
            </Typography>
          }
          subheader={
            <Typography variant="subtitle1" textAlign="center" color="textSecondary">
              {isSignUp ? 'Create your account' : 'Sign in to your account'}
            </Typography>
          }
        />
        <CardContent>
          {mfaChallengeRequired && (
            <form onSubmit={handleMfaVerify}>
              <Stack spacing={3}>
                {error && <Alert severity="error">{error}</Alert>}
                <Alert severity="info">Enter the 6-digit code from your authenticator app</Alert>

                <TextField
                  label="MFA Code"
                  type="text"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  fullWidth
                  variant="outlined"
                  placeholder="000000"
                  autoComplete="one-time-code"
                  inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={loading || mfaCode.length !== 6}
                >
                  {loading ? 'Verifying...' : 'Verify'}
                </Button>

                <Button
                  variant="text"
                  onClick={() => {
                    clearMfaChallengeState();
                    setMfaCode('');
                    setError(null);
                    setMfaFailedAttempts(0);
                    setMfaCooldownUntilMs(0);
                  }}
                  disabled={loading}
                >
                  Back to Login
                </Button>
              </Stack>
            </form>
          )}

          {!mfaChallengeRequired && (
            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                {error && <Alert severity="error">{error}</Alert>}

                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                  variant="outlined"
                  autoComplete="email"
                />

                {!useMagic && (
                  <TextField
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    fullWidth
                    variant="outlined"
                    autoComplete="current-password"
                  />
                )}

                <Button type="submit" variant="contained" fullWidth size="large" disabled={loading}>
                  {loading
                    ? 'Loading...'
                    : useMagic
                      ? 'Email me a sign-in link'
                      : isSignUp
                        ? 'Sign Up'
                        : 'Sign In'}
                </Button>

                {/* OAuth providers - only show if enabled in environment */}
                {import.meta.env.VITE_ENABLE_OAUTH === 'true' && (
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    startIcon={<GoogleIcon />}
                    onClick={() => signInWithGoogle()}
                    disabled={loading}
                  >
                    Continue with Google
                  </Button>
                )}

                {/* Hide signup toggle for invite-only mode - always hidden in production */}

                {!useMagic && (
                  <Button variant="text" onClick={() => setResetOpen(true)} disabled={loading}>
                    Forgot your password?
                  </Button>
                )}
              </Stack>
            </form>
          )}
        </CardContent>
      </Card>
      <Dialog open={resetOpen} onClose={() => setResetOpen(false)}>
        <DialogTitle>Reset your password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              We will email you a link to reset your password.
            </Typography>
            <TextField
              label="Email"
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              required
              fullWidth
              autoComplete="email"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSendReset} disabled={loading || !resetEmail}>
            Send Reset Email
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
